// Number extraction + cross-section consistency (PRD §4a, §11.4).
//
// Sprint-3 scope:
//   - Extract sample sizes (n=), percentages, p-values, 95% CIs, mean±SD
//   - Cross-section sample-size consistency: flag pairs of sample-size mentions
//     in abstract vs methods vs results that differ by >5% without explanation
//   - p-value reporting completeness: flag "P<0.05" without an exact value
//   - Boutron pattern subset: spin words in abstract without effect size

import type { ManuscriptDocument } from '../pdf/types';
import { sectionAt } from '../pdf/sections';
import { offsetInTabularRegion } from '../pdf/tables';
import { stableFindingId } from './finding-id';
import type { NumberMention, PatternFinding } from './types';

interface Pattern {
  type: NumberMention['type'];
  re: RegExp;
  /** Index of the numeric capture group in the match. */
  group: number;
}

const PATTERNS: Pattern[] = [
  { type: 'sample_size', re: /\bn\s*=\s*(\d{1,7})\b/gi, group: 1 },
  // P2-7: a *declared study total* written in prose with no "n=", anchored
  // to a study-unit noun. This captures the headline N (abstracts routinely
  // say "A total of 331 responses were collected" with no n=) while NOT
  // capturing power-calculation targets ("a target sample size of 300
  // participants"), subgroup counts, or non-subject totals ("a total of 12
  // studies"). A looser `sample (size) of N` / bare `N participants …
  // enrolled` form was tried and rejected: on a real manuscript it read the
  // Methods power-calc target (300) as the study N and produced a false
  // "abstract 331 vs methods 300" mismatch — the exact false-flag class
  // this engine must never emit. Restores the genuine abstract↔results
  // total-consistency check with no real-PDF false positives.
  {
    type: 'sample_size',
    re: /\b(?:a\s+)?total\s+of\s+(\d{2,7})\s+(?:participants?|patients?|subjects?|respondents?|responses?|cases?|records?|individuals?|adults?|children|women|men)\b/gi,
    group: 1,
  },
  { type: 'p_value', re: /\bp\s*[<>=≤≥]\s*(0?\.\d+|\d+\.?\d*e?-?\d*)\b/gi, group: 1 },
  { type: 'percentage', re: /(\d+(?:\.\d+)?)\s*%/g, group: 1 },
  {
    type: 'ci_lower',
    re: /95\s*%?\s*(?:CI|confidence\s+interval)[:,\s]*\(?\s*(-?\d+\.?\d*)\s*(?:to|[-–])\s*(-?\d+\.?\d*)/gi,
    group: 1,
  },
];

export function extractNumbers(doc: ManuscriptDocument): NumberMention[] {
  const out: NumberMention[] = [];
  for (const pat of PATTERNS) {
    pat.re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = pat.re.exec(doc.rawText)) !== null) {
      const raw = m[pat.group];
      const value = Number.parseFloat(raw);
      if (!Number.isFinite(value)) continue;
      const offset = m.index;
      const context = contextAround(doc.rawText, offset, m[0].length);
      out.push({
        value,
        raw: m[0],
        context,
        type: pat.type,
        section: sectionAt(doc.sections, offset),
        offset,
      });
      if (pat.type === 'ci_lower') {
        const upper = Number.parseFloat(m[2]);
        if (Number.isFinite(upper)) {
          out.push({
            value: upper,
            raw: m[0],
            context,
            type: 'ci_upper',
            section: sectionAt(doc.sections, offset),
            offset,
          });
        }
      }
    }
  }
  return out;
}

function contextAround(text: string, offset: number, matchLen: number): string {
  const before = Math.max(0, offset - 40);
  const after = Math.min(text.length, offset + matchLen + 40);
  return text.slice(before, after).replace(/\s+/g, ' ').trim();
}

const SPIN_PHRASES = [
  'significantly improved',
  'clinically meaningful',
  'highly significant',
  'striking improvement',
  'dramatic improvement',
  'markedly improved',
  'substantially improved',
];

export function buildNumberFindings(
  doc: ManuscriptDocument,
  numbers: NumberMention[],
): PatternFinding[] {
  const findings: PatternFinding[] = [];

  // 1. Sample-size cross-section consistency.
  //
  // Only *declared totals* should be compared. A subgroup count is written
  // tied to a percentage — "the majority were female (79%, n=261) and
  // students (82%, n=271)" — and must NOT be read as the study's sample
  // size (doing so produced a false RED "271 vs 331 mismatch" when the
  // abstract's true total was the prose "A total of 331 responses").
  const samples = numbers.filter(
    (n) =>
      n.type === 'sample_size' &&
      !isSubgroupCount(n) &&
      // P0-3: a flattened table/figure cell must not masquerade as the
      // declared sample size in the prose cross-section comparison.
      !offsetInTabularRegion(n.offset, doc.tableRegions),
  );
  const bySection = new Map<string, NumberMention[]>();
  for (const n of samples) {
    const arr = bySection.get(n.section) ?? [];
    arr.push(n);
    bySection.set(n.section, arr);
  }
  // Compare the largest n in abstract vs methods vs results.
  const tops: Record<string, NumberMention | undefined> = {
    abstract: maxBy(bySection.get('abstract') ?? [], (n) => n.value),
    methods: maxBy(bySection.get('methods') ?? [], (n) => n.value),
    results: maxBy(bySection.get('results') ?? [], (n) => n.value),
  };
  const presentSections = Object.entries(tops).filter(([, v]) => v != null) as Array<
    [string, NumberMention]
  >;
  for (let i = 0; i < presentSections.length; i++) {
    for (let j = i + 1; j < presentSections.length; j++) {
      const [sA, nA] = presentSections[i];
      const [sB, nB] = presentSections[j];
      const lo = Math.min(nA.value, nB.value);
      const hi = Math.max(nA.value, nB.value);
      if (lo === 0) continue;
      const diff = (hi - lo) / lo;
      if (diff > 0.05) {
        const findingText = `Sample-size mismatch: ${sA} reports n=${nA.value}, ${sB} reports n=${nB.value} (${Math.round(diff * 100)}% difference).`;
        findings.push({
          id: stableFindingId('stats', findingText, nA.offset),
          category: 'stats',
          severity: diff > 0.2 ? 'red' : 'amber',
          checklistItemId: null,
          findingText,
          evidencePassage: nA.context,
          evidenceOffset: nA.offset,
          section: nA.section,
          layer: 'pattern',
        });
      }
    }
  }

  // 2. p-value reporting completeness: P<0.05 without an exact value nearby.
  const inexactP = numbers.filter((n) => n.type === 'p_value' && /[<>≤≥]/.test(n.raw));
  for (const n of inexactP) {
    // `p < 0.001` (and smaller, e.g. p < 0.0001) is the standard,
    // journal-recommended way to report a very small p-value (ICMJE / SAMPL
    // guidance) — reporting "p = 0.00037" instead is actively discouraged.
    // It is NOT imprecise reporting. Flagging it produced ~10 false AMBER
    // findings on a normal epidemiology manuscript (every "p < 0.001"),
    // which then inflated the decision rubric. Only threshold reports where
    // an exact value would genuinely add information (p < 0.05, p < 0.01,
    // p > 0.05) remain flagged.
    if (/[<≤]/.test(n.raw) && n.value <= 0.001) continue;
    // Heuristic: if the same paragraph contains an exact p= value, skip.
    const para = doc.rawText
      .slice(Math.max(0, n.offset - 200), Math.min(doc.rawText.length, n.offset + 200))
      .toLowerCase();
    if (/p\s*=\s*0?\.\d+/.test(para)) continue;
    const findingText = `Inexact p-value reported (${n.raw}) without exact value in the surrounding passage.`;
    findings.push({
      id: stableFindingId('stats', findingText, n.offset),
      category: 'stats',
      severity: 'amber',
      checklistItemId: null,
      findingText,
      evidencePassage: n.context,
      evidenceOffset: n.offset,
      section: n.section,
      layer: 'pattern',
    });
  }

  // 3. Boutron spin pattern subset: spin phrases in abstract without CI/effect-size nearby.
  const abstractSpan = doc.sections.find((s) => s.section === 'abstract');
  if (abstractSpan) {
    const abstractText = doc.rawText.slice(abstractSpan.start, abstractSpan.end);
    const lower = abstractText.toLowerCase();
    const hasEffect = /(95\s*%\s*(ci|confidence\s+interval)|hazard\s+ratio|odds\s+ratio|risk\s+ratio|mean\s+difference)/i.test(
      abstractText,
    );
    for (const phrase of SPIN_PHRASES) {
      let idx = lower.indexOf(phrase);
      while (idx >= 0) {
        if (!hasEffect) {
          const findingText = `Abstract claims "${phrase}" without accompanying effect estimate or confidence interval.`;
          findings.push({
            id: stableFindingId('stats', findingText, abstractSpan.start + idx),
            category: 'stats',
            severity: 'amber',
            checklistItemId: null,
            findingText,
            evidencePassage: contextAround(abstractText, idx, phrase.length),
            evidenceOffset: abstractSpan.start + idx,
            section: 'abstract',
            layer: 'pattern',
          });
        }
        idx = lower.indexOf(phrase, idx + phrase.length);
      }
    }
  }

  return findings;
}

/**
 * True when an n= mention is a subgroup count rather than a declared sample
 * size — i.e. a percentage sits immediately next to it on either side:
 *   "female (79%, n=261)"  |  "n=261 (79%)"  |  "82%, n=271"
 * Uses the ±40-char `context` the extractor already captured.
 */
function isSubgroupCount(n: NumberMention): boolean {
  const ctx = n.context;
  // Locate the n=<value> token within the (whitespace-collapsed) context.
  const tok = new RegExp(`\\bn\\s*=\\s*${n.value}\\b`, 'i');
  const mm = tok.exec(ctx);
  if (!mm) {
    // Fallback: any % within the whole short context.
    return /\d{1,3}(?:\.\d+)?\s*%/.test(ctx);
  }
  const start = mm.index;
  const end = mm.index + mm[0].length;
  const before = ctx.slice(Math.max(0, start - 14), start);
  const after = ctx.slice(end, end + 14);
  return /\d{1,3}(?:\.\d+)?\s*%[)\s,;]*$/.test(before) || /^[)\s,;]*\(?\s*\d{1,3}(?:\.\d+)?\s*%/.test(after);
}

function maxBy<T>(arr: T[], fn: (t: T) => number): T | undefined {
  if (arr.length === 0) return undefined;
  let best = arr[0];
  let bestScore = fn(best);
  for (let i = 1; i < arr.length; i++) {
    const s = fn(arr[i]);
    if (s > bestScore) {
      best = arr[i];
      bestScore = s;
    }
  }
  return best;
}
